import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  TablePagination,
  Button,
  Alert,
  Box,
  CircularProgress,
  Tooltip,
  Collapse 
} from '@mui/material';
import { Check, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import dateFns, { format } from 'date-fns';

const parseAndFormatTimestamp = (str) => {
  const [, y, M, d, h, m] = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):/) || [];
  return `${d}.${M}.${y} ${h}:${m}`;
};

const parseAndFormatDate = (str) => {
  const [, y, M, d] = str.match(/^(\d{4})-(\d{2})-(\d{2})T/) || [];
  return `${d}.${M}.${y}`;
};

// Default formatters
const defaultFormatters = {
  date: (value, pattern = 'dd.MM.yyyy') => {
    if (!value) return '';

    // If date provided with some time zone we need to just display this in provided tz
    const tzRegex = /[+-]\d{2}:\d{2}$/;
    if (typeof value === 'string' && tzRegex.test(value)) {
      return parseAndFormatDate(value);
    }
    return format(new Date(value), pattern);
  },
  timestamp: (value, pattern = 'dd.MM.yyyy HH:mm') => {
    if (!value) return '';

    // If date provided with some time zone we need to just display this in provided tz
    const tzRegex = /[+-]\d{2}:\d{2}$/;
    if (typeof value === 'string' && tzRegex.test(value)) {
      return parseAndFormatTimestamp(value);
    }

    return format(new Date(value), pattern);
  },
  currency: (value) => value.toFixed(2),
  number: (value) => value.toFixed(2),
  boolean: (value) => (value? <Check /> : ''),
  default: (value) => value
};

const TableComponent = ({
  columns,
  getData,
  renderCell,
  actionsHandlers = {},
  formatters = defaultFormatters,
  pagination = true,
  rowsPerPageOptions = [5, 10, 25],
  forceUpdate = 0,
  resetPage = false,
  onDataLoaded,
  expandableColumn = 'expand',
  canExpand,
  expandedContent = () => null,
  customHeaderCellStyle = {},
  tableComponent = Paper,
  height
}) => {

  const theme = useTheme();

  const [data, setData] = useState({
    page: 0,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
    rows: []
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null)

  // Header styles
  const headerCellStyle = {
    textAlign: 'left',
    fontWeight: 'bold',
    py: 0.5,
    backgroundColor: theme.palette.secondary.main,
    ...customHeaderCellStyle
  };

  const handleChangePage = (event, newPage) => {
    if (newPage < 0) newPage = 0;
    if (newPage >= data.totalPages) newPage = data.totalPages - 1;
    setData({
      ...data,
      page: newPage
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setData({
      ...data,
      limit: newLimit,
      page: 0
    });
  };

  const handleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  useEffect(() => {
    let timeoutId;

    const showLoadingAfterDelay = () => {
      timeoutId = setTimeout(() => {
        if (!isLoading) {
          setIsLoading(true);
        }
      }, 1000);
    };

    showLoadingAfterDelay();

    const currentPage = resetPage ? 0 : data.page;

    getData(currentPage, data.limit)
      .then((processedData) => {
        setData(processedData);
        setError(null);
        setExpanded(null); // collapse everything on data update
        if (onDataLoaded) {
          onDataLoaded();
        }
      })
      .catch((error) => {
        console.error('Data loading error:', error);
        if (error.response && error.response.data && error.response.data.msg) {
          setError(error.response.data.msg);
        } else {
          setError(error.message);
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setIsLoading(false);
      });

    return () => {
      clearTimeout(timeoutId);
    };

  }, [data.page, data.limit, forceUpdate]);

  const getFormattedValue = (value, column) => {
    if (column.format) {
      return column.format(value);
    }
    if (column.type && formatters[column.type]) {
      return formatters[column.type](value);
    }
    return value;
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => {
        return acc?.[part];
    }, obj);
    };

  // Render cell
  const renderTableData = (row, column) => {
    if (actionsHandlers[column.id]) {
      return (
        <div style={{ display: 'flex' }}>
          {row[column.id]}
          {actionsHandlers[column.id].map((action) => (
            <>
              {(!action.visible || action.visible(row)) && (
              <Tooltip key={action.name} title={action.tooltip}>
                  <Button
                  key={action.name}
                  onClick={() => action.handler(row)}
                  >
                  {action.label}
                  </Button>
              </Tooltip>
              )}
            </>
          ))}
        </div>
      );
    }

    if (column.id === expandableColumn && (!canExpand || canExpand(row))) {
      return (
        <Button 
          onClick={() => handleExpand(row.id)}
          size="small"
        >
          {expanded === row.id ? <ExpandLess /> : <ExpandMore />}
        </Button>
      );
    }

    const cellContent = renderCell ? renderCell(row, column) : getFormattedValue(getNestedValue(row, column.id), column);

    if (column.action) {
      return (
        <div
          onClick={() => column.action.handler(row)}
          style={{ cursor: 'pointer', display: 'inline-block' }}
          >
          {cellContent}
        </div>
      );
    }

    return cellContent;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '0px',
        ...(height ? { height } : {})
      }}>
        {isLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1300,
            }}
          >
            <CircularProgress />
          </Box>
                )}
        {error && <Alert severity="error">{error}</Alert>}
        <TableContainer component={tableComponent} sx={{ width: '100%', tableLayout: 'auto', maxWidth: 'none', overflow: 'auto', height: '100%', maxHeight: '100%' }}>
        <Table stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                  {columns.map((column) => (
                  <TableCell key={column.id} sx={headerCellStyle}>{column.label}</TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
            {data.rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow
                  key={row.id}
                >
                {columns.map((column) => (
                    <TableCell key={column.id} sx={{
                        py: 0.5,
                        width: column.width || 'auto',
                        maxWidth: '300px',
                        overflow: 'auto',
                        wordBreak: 'break-word'
                      }}>
                      {renderTableData(row, column)}
                    </TableCell>
                ))}
                </TableRow>

                {/* Expandable part */}
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length}>
                    <Collapse 
                      in={expanded === row.id} 
                      timeout="auto" 
                      unmountOnExit
                    >
                      <Box sx={{ margin: 0 }}>
                        {expandedContent(row)}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            </TableBody>
        </Table>
        </TableContainer>

        {pagination && (
          <Box sx={{
            width: '100%',
            mt: 2,
            display: 'flex', 
            justifyContent: 'center'
          }}>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              count={data.totalCount}
              rowsPerPage={data.limit}
              page={data.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              />
          </Box>
        )}
    </Box>
  );
};

export default TableComponent;
